import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useLocale } from '@/contexts/LocaleContext';
import { useAuth } from '@/contexts/AuthContext';
import { getSample, updateSampleStatus } from '@/lib/api';
import { 
  ArrowLeft, 
  Package, 
  Building2, 
  Calendar,
  MapPin,
  Loader2,
  CheckCircle,
  XCircle,
  Truck,
  DollarSign
} from 'lucide-react';
import { toast } from 'sonner';

const statusConfig = {
  solicitada: { label: 'Solicitada', labelEn: 'Requested', color: 'bg-blue-100 text-blue-800' },
  aprobada: { label: 'Aprobada', labelEn: 'Approved', color: 'bg-green-100 text-green-800' },
  enviada: { label: 'Enviada', labelEn: 'Shipped', color: 'bg-purple-100 text-purple-800' },
  recibida: { label: 'Recibida', labelEn: 'Received', color: 'bg-emerald-100 text-emerald-800' },
  rechazada: { label: 'Rechazada', labelEn: 'Rejected', color: 'bg-red-100 text-red-800' },
};

const SampleDetail = () => {
  const { sampleId } = useParams();
  const navigate = useNavigate();
  const { isUSA } = useLocale();
  const { user } = useAuth();
  const [sample, setSample] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [sampleCost, setSampleCost] = useState('');
  const [shippingCost, setShippingCost] = useState('');
  const [responseNote, setResponseNote] = useState('');

  useEffect(() => {
    const fetchSample = async () => {
      try {
        const data = await getSample(sampleId);
        setSample(data);
      } catch (error) {
        console.error('Error fetching sample:', error);
        toast.error(isUSA ? 'Error loading sample request' : 'Error al cargar solicitud de muestra');
      } finally {
        setLoading(false);
      }
    };
    fetchSample();
  }, [sampleId, isUSA]);

  const isProvider = user?.company_id === sample?.provider_company_id;
  const isRequester = user?.company_id === sample?.requester_company_id;

  const handleStatusUpdate = async (newStatus) => {
    setUpdating(true);
    try {
      await updateSampleStatus(
        sampleId, 
        newStatus, 
        sampleCost ? parseFloat(sampleCost) : undefined,
        shippingCost ? parseFloat(shippingCost) : undefined
      );
      setSample(prev => ({ ...prev, status: newStatus }));
      toast.success(isUSA ? 'Status updated' : 'Estado actualizado');
    } catch (error) {
      toast.error(isUSA ? 'Error updating status' : 'Error al actualizar estado');
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (!sample) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">{isUSA ? 'Sample request not found' : 'Solicitud de muestra no encontrada'}</p>
        <Button variant="outline" onClick={() => navigate(-1)} className="mt-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          {isUSA ? 'Go back' : 'Volver'}
        </Button>
      </div>
    );
  }

  const status = statusConfig[sample.status] || statusConfig.solicitada;

  return (
    <div className="space-y-6 max-w-4xl mx-auto" data-testid="sample-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-slate-900">
              {isUSA ? 'Sample Request' : 'Solicitud de Muestra'}
            </h1>
            <Badge className={status.color}>
              {isUSA ? status.labelEn : status.label}
            </Badge>
          </div>
          <p className="text-sm text-slate-500">ID: {sample.sample_id}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Product Details */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="w-5 h-5 text-emerald-600" />
                {isUSA ? 'Product Details' : 'Detalles del Producto'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-slate-500">{isUSA ? 'Product' : 'Producto'}</label>
                  <p className="font-medium">{sample.product_name || sample.product_id}</p>
                </div>
                <div>
                  <label className="text-sm text-slate-500">{isUSA ? 'Quantity' : 'Cantidad'}</label>
                  <p className="font-medium">{sample.quantity} {sample.unit || 'metros'}</p>
                </div>
                {sample.color && (
                  <div>
                    <label className="text-sm text-slate-500">{isUSA ? 'Color' : 'Color'}</label>
                    <p className="font-medium">{sample.color}</p>
                  </div>
                )}
              </div>
              {sample.notes && (
                <div>
                  <label className="text-sm text-slate-500">{isUSA ? 'Notes' : 'Notas'}</label>
                  <p className="mt-1 text-slate-700">{sample.notes}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Response Section - Only for Provider */}
          {isProvider && sample.status === 'solicitada' && (
            <Card>
              <CardHeader>
                <CardTitle>{isUSA ? 'Respond to Request' : 'Responder Solicitud'}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium">{isUSA ? 'Sample Cost (MXN)' : 'Costo de Muestra (MXN)'}</label>
                    <Input
                      type="number"
                      placeholder="0.00"
                      value={sampleCost}
                      onChange={(e) => setSampleCost(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">{isUSA ? 'Shipping Cost (MXN)' : 'Costo de Envío (MXN)'}</label>
                    <Input
                      type="number"
                      placeholder="0.00"
                      value={shippingCost}
                      onChange={(e) => setShippingCost(e.target.value)}
                    />
                  </div>
                </div>
                <Textarea
                  placeholder={isUSA ? 'Additional notes...' : 'Notas adicionales...'}
                  value={responseNote}
                  onChange={(e) => setResponseNote(e.target.value)}
                />
                <div className="flex gap-3">
                  <Button 
                    onClick={() => handleStatusUpdate('aprobada')}
                    disabled={updating}
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    {updating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                    {isUSA ? 'Approve' : 'Aprobar'}
                  </Button>
                  <Button 
                    variant="destructive"
                    onClick={() => handleStatusUpdate('rechazada')}
                    disabled={updating}
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    {isUSA ? 'Reject' : 'Rechazar'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Mark as Shipped - Provider after approval */}
          {isProvider && sample.status === 'aprobada' && (
            <Card>
              <CardContent className="pt-6">
                <Button 
                  onClick={() => handleStatusUpdate('enviada')}
                  disabled={updating}
                  className="w-full"
                >
                  <Truck className="w-4 h-4 mr-2" />
                  {isUSA ? 'Mark as Shipped' : 'Marcar como Enviada'}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Confirm Receipt - Requester */}
          {isRequester && sample.status === 'enviada' && (
            <Card>
              <CardContent className="pt-6">
                <Button 
                  onClick={() => handleStatusUpdate('recibida')}
                  disabled={updating}
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  {isUSA ? 'Confirm Receipt' : 'Confirmar Recepción'}
                </Button>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">{isUSA ? 'Request Info' : 'Info de Solicitud'}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-start gap-3">
                <Building2 className="w-5 h-5 text-slate-400 mt-0.5" />
                <div>
                  <p className="text-sm text-slate-500">{isUSA ? 'Requester' : 'Solicitante'}</p>
                  <p className="font-medium">{sample.requester_company_name}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Building2 className="w-5 h-5 text-slate-400 mt-0.5" />
                <div>
                  <p className="text-sm text-slate-500">{isUSA ? 'Provider' : 'Proveedor'}</p>
                  <p className="font-medium">{sample.provider_company_name}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Calendar className="w-5 h-5 text-slate-400 mt-0.5" />
                <div>
                  <p className="text-sm text-slate-500">{isUSA ? 'Date' : 'Fecha'}</p>
                  <p className="font-medium">
                    {new Date(sample.created_at).toLocaleDateString(isUSA ? 'en-US' : 'es-MX')}
                  </p>
                </div>
              </div>
              {sample.shipping_address && (
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-slate-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-slate-500">{isUSA ? 'Shipping Address' : 'Dirección de Envío'}</p>
                    <p className="font-medium">{sample.shipping_address}</p>
                  </div>
                </div>
              )}
              {(sample.sample_cost || sample.shipping_cost) && (
                <div className="flex items-start gap-3">
                  <DollarSign className="w-5 h-5 text-slate-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-slate-500">{isUSA ? 'Costs' : 'Costos'}</p>
                    {sample.sample_cost && <p className="font-medium">Muestra: ${sample.sample_cost} MXN</p>}
                    {sample.shipping_cost && <p className="font-medium">Envío: ${sample.shipping_cost} MXN</p>}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default SampleDetail;
